import { useEffect, useState, useCallback } from 'react';
import type { TargetRect } from './useTargetPosition';
import { useTourStepPresence } from '../../stores/tour-presence.store';

interface SpotlightOverlayProps {
  targetSelector?: string;
  onClick?: () => void;
  borderRadius?: number;
  padding?: number;
  disableAutoScroll?: boolean;
}

const PADDING = 8;
const BG = 'rgba(0, 0, 0, 0.5)';
const BORDER_RADIUS = 16;

/**
 * A visual dim/click-blocker region of the spotlight. Clicking anywhere on
 * the dimmed area advances onboarding as a mouse convenience — but it is NOT
 * the accessible advance control. Keyboard/AT users advance via the real
 * "Continue" / "Next" / "Skip Tour" button inside the speech bubble. Making
 * these regions focusable buttons would inject 4-5 phantom "Continue"
 * controls into the tab order and screen-reader tree, which is worse than a
 * presentational backdrop. So they are `aria-hidden` and presentational.
 */
function ClickBlocker({ onClick, style }: { onClick?: () => void; style: React.CSSProperties }) {
  return (
    <div
      aria-hidden="true"
      role="presentation"
      className="fixed z-[200] cursor-default"
      style={style}
      onClick={onClick}
    />
  );
}

/**
 * Spotlight overlay that dims the screen with a rounded transparent cutout
 * over a target element.
 *
 * Uses box-shadow for the rounded visual cutout + 4 transparent divs for
 * click blocking outside the cutout.
 */
export default function SpotlightOverlay({
  targetSelector,
  onClick,
  borderRadius: borderRadiusProp,
  padding: paddingProp,
  disableAutoScroll,
}: SpotlightOverlayProps) {
  // A tour step is on screen: toasts keep out of its way (app/toastConfig.ts).
  useTourStepPresence(true);
  const pad = paddingProp ?? PADDING;
  const radius = borderRadiusProp ?? BORDER_RADIUS;
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  const updateRect = useCallback(() => {
    if (!targetSelector) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(targetSelector);
    if (!el) {
      setTargetRect(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    setTargetRect({
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    });
  }, [targetSelector, pad]);

  useEffect(() => {
    if (!targetSelector) return;

    let attempts = 0;
    const maxAttempts = 180;
    let rafId: number;
    let scrollTimeoutId: ReturnType<typeof setTimeout>;

    let scrolled = false;

    const poll = () => {
      const el = document.querySelector(targetSelector);
      if (el && !scrolled) {
        scrolled = true;
        if (!disableAutoScroll) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Wait for scroll to settle before measuring
          scrollTimeoutId = setTimeout(updateRect, 350);
        }
      }
      updateRect();
      if (!el && attempts < maxAttempts) {
        attempts++;
        rafId = requestAnimationFrame(poll);
      }
    };

    poll();

    const observer = new ResizeObserver(() => {
      updateRect();
    });

    const el = document.querySelector(targetSelector);
    if (el) observer.observe(el);

    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(scrollTimeoutId);
      observer.disconnect();
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetSelector, updateRect, disableAutoScroll]);

  // No target: render full-screen dim overlay
  if (!targetRect) {
    return (
      <ClickBlocker
        onClick={onClick}
        style={{ inset: 0, backgroundColor: BG, transition: 'all 300ms' }}
      />
    );
  }

  const { top, left, width, height } = targetRect;

  return (
    <>
      {/* Rounded visual cutout using box-shadow with feathered edge */}
      <div
        className="fixed z-[200] pointer-events-none transition-all duration-300"
        style={{
          top,
          left,
          width,
          height,
          borderRadius: radius,
          boxShadow: `0 0 6px 9999px ${BG}`,
        }}
      />

      {/* Click-blocker regions (transparent, block clicks outside the cutout) */}
      {/* TOP */}
      <ClickBlocker
        onClick={onClick}
        style={{ top: 0, left: 0, width: '100vw', height: Math.max(0, top) }}
      />
      {/* LEFT */}
      <ClickBlocker onClick={onClick} style={{ top, left: 0, width: Math.max(0, left), height }} />
      {/* RIGHT */}
      <ClickBlocker
        onClick={onClick}
        style={{
          top,
          left: left + width,
          width: `max(0px, calc(100vw - ${left + width}px))`,
          height,
        }}
      />
      {/* BOTTOM */}
      <ClickBlocker
        onClick={onClick}
        style={{
          top: top + height,
          left: 0,
          width: '100vw',
          height: `max(0px, calc(100vh - ${top + height}px))`,
        }}
      />
    </>
  );
}
